import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, ChevronDown } from 'lucide-react';

const FAQItem = ({ question, answer, isOpen, toggle }) => {
  return (
    <div className="faq-item">
      <button 
        className="faq-question"
        onClick={toggle}
      >
        <h3 className="heading-3">{question}</h3>
        <ChevronDown className={`faq-icon ${isOpen ? 'open' : ''}`} size={20} />
      </button>
      {isOpen && (
        <div className="faq-answer">
          <p className="body-medium">{answer}</p>
        </div>
      )}
    </div>
  );
};

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  
  const [openFAQ, setOpenFAQ] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    const whatsappMessage = `Hola, soy ${formData.name}. ${formData.message}`;
    const whatsappUrl = `https://wa.me/51934442759?text=${encodeURIComponent(whatsappMessage)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  
  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };
  
  const faqs = [
    {
      question: "¿Cuánto dura una sesión de terapia?",
      answer: "Cada sesión tiene una duración aproximada de 45 a 60 minutos, dependiendo del tipo de terapia y las necesidades del paciente."
    },
    {
      question: "¿Cómo puedo agendar una cita?",
      answer: "Puedes agendar tu cita escribiéndonos por WhatsApp al 934 442 759. Nuestro equipo te responderá a la brevedad para coordinar el horario que mejor se ajuste a tus necesidades."
    },
    {
      question: "¿Ofrecen terapia online?",
      answer: "Sí, ofrecemos sesiones de terapia online a través de videollamada para mayor comodidad de nuestros pacientes."
    },
    {
      question: "¿Trabajan con niños y adolescentes?",
      answer: "Sí, contamos con especialistas en psicología infantil y adolescente, así como en neuropsicología para trastornos del neurodesarrollo."
    },
    {
      question: "¿Las sesiones son confidenciales?",
      answer: "Absolutamente. Toda la información compartida en las sesiones es estrictamente confidencial, siguiendo el código de ética profesional."
    }
  ];

  return (
    <div className="contact-page" data-testid="contact-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <h1 className="heading-hero">Contacto</h1>
          <p className="hero-subtitle body-large">
            Estamos aquí para ayudarte. Contáctanos y comienza tu proceso hoy mismo.
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="contact-section">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Info */}
            <div className="contact-info-container">
              <h2 className="heading-2">Información de Contacto</h2>
              <p className="body-medium">
                Puedes comunicarte con nosotros a través de cualquiera de estos medios.
              </p>

              <div className="contact-info-list">
                <div className="contact-info-item">
                  <div className="contact-icon">
                    <Phone size={24} />
                  </div>
                  <div>
                    <h3 className="heading-3">WhatsApp</h3>
                    <a href="https://wa.me/51934442759" className="body-medium">
                      934 442 759
                    </a>
                  </div>
                </div>

                <div className="contact-info-item">
                  <div className="contact-icon">
                    <Mail size={24} />
                  </div>
                  <div>
                    <h3 className="heading-3">Email</h3>
                    <a href="mailto:mindlifeperu@gmail.com" className="body-medium">
                      mindlifeperu@gmail.com
                    </a>
                  </div>
                </div>

                <div className="contact-info-item">
                  <div className="contact-icon">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <h3 className="heading-3">Ubicación</h3>
                    <p className="body-medium">Lima, Perú</p>
                  </div>
                </div>

                <div className="contact-info-item">
                  <div className="contact-icon">
                    <Clock size={24} />
                  </div>
                  <div>
                    <h3 className="heading-3">Horario</h3>
                    <p className="body-medium">Lunes a Sábado: 9:00 AM - 8:00 PM</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form-container">
              <h2 className="heading-2">Envíanos un Mensaje</h2>
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name" className="body-small">Nombre Completo *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="Tu nombre"
                    data-testid="contact-input-name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="body-small">Correo Electrónico *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="tu@email.com"
                    data-testid="contact-input-email"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone" className="body-small">Teléfono</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="999 999 999"
                    data-testid="contact-input-phone"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="body-small">Mensaje *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="form-input"
                    placeholder="Cuéntanos cómo podemos ayudarte..."
                    data-testid="contact-input-message"
                  ></textarea>
                </div>

                <button type="submit" className="btn-primary full-width" data-testid="contact-submit-btn">
                  <Send size={18} />
                  ENVIAR POR WHATSAPP
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="faq-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Preguntas Frecuentes</h2>
            <p className="body-large">Respuestas a las dudas más comunes</p>
          </div>

          <div className="faq-container">
            {faqs.map((faq, idx) => (
              <FAQItem
                key={idx}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === idx}
                toggle={() => toggleFAQ(idx)}
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
